import type { Rect, Vec2 } from './model/types';

/**
 * Selection rectangle maths — pure, so marquee behaviour is unit-tested rather
 * than eyeballed. All coordinates are document pixels; rectangles are always
 * normalised (non-negative width/height) once produced.
 */

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const HANDLES: readonly HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** Cursor for each handle, used by the overlay. */
export const HANDLE_CURSORS: Record<HandleId, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
};

export function rectFromPoints(a: Vec2, b: Vec2): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

export function normalizeRect(rect: Rect): Rect {
  return {
    x: Math.min(rect.x, rect.x + rect.width),
    y: Math.min(rect.y, rect.y + rect.height),
    width: Math.abs(rect.width),
    height: Math.abs(rect.height),
  };
}

export function rectEdges(rect: Rect) {
  return {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height,
  };
}

export function rectEquals(a: Rect | null, b: Rect | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    Math.abs(a.x - b.x) < 1e-6 &&
    Math.abs(a.y - b.y) < 1e-6 &&
    Math.abs(a.width - b.width) < 1e-6 &&
    Math.abs(a.height - b.height) < 1e-6
  );
}

export function isInsideRect(rect: Rect, point: Vec2): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  );
}

/** Keep a rectangle inside `bounds`, shrinking it rather than moving it. */
export function clampRect(rect: Rect, bounds: Rect): Rect {
  const left = Math.max(bounds.x, Math.min(rect.x, bounds.x + bounds.width));
  const top = Math.max(bounds.y, Math.min(rect.y, bounds.y + bounds.height));
  const right = Math.max(left, Math.min(rect.x + rect.width, bounds.x + bounds.width));
  const bottom = Math.max(top, Math.min(rect.y + rect.height, bounds.y + bounds.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function moveRect(rect: Rect, delta: Vec2, bounds: Rect): Rect {
  // A selection never leaves the document: it is pushed back at the edge.
  const minX = bounds.x;
  const minY = bounds.y;
  const maxX = bounds.x + bounds.width - rect.width;
  const maxY = bounds.y + bounds.height - rect.height;
  return {
    ...rect,
    x: Math.max(minX, Math.min(rect.x + delta.x, Math.max(minX, maxX))),
    y: Math.max(minY, Math.min(rect.y + delta.y, Math.max(minY, maxY))),
  };
}

/** Which handle is under `point`, or null. Tolerance is in screen-ish pixels. */
export function hitTestHandle(rect: Rect, point: Vec2, tolerance = 8): HandleId | null {
  const { left, top, right, bottom } = rectEdges(rect);
  const withinX = Math.abs(point.x - left) <= tolerance;
  const withinRight = Math.abs(point.x - right) <= tolerance;
  const withinY = Math.abs(point.y - top) <= tolerance;
  const withinBottom = Math.abs(point.y - bottom) <= tolerance;

  if (withinY && withinX) return 'nw';
  if (withinY && withinRight) return 'ne';
  if (withinBottom && withinRight) return 'se';
  if (withinBottom && withinX) return 'sw';
  if (withinY) return 'n';
  if (withinBottom) return 's';
  if (withinX) return 'w';
  if (withinRight) return 'e';
  return null;
}

export interface ResizeOptions {
  /** Lock the width/height ratio while dragging a corner. */
  aspect?: number;
  minimum?: number;
}

/**
 * Resize a rectangle by dragging one handle.
 *
 * Opposite edge stays put, the result is normalised (so dragging past the
 * opposite edge flips cleanly) and then clamped to the document.
 */
export function resizeRect(
  rect: Rect,
  handle: HandleId,
  delta: Vec2,
  bounds: Rect,
  options: ResizeOptions = {},
): Rect {
  const minimum = options.minimum ?? 1;
  const { left, top, right, bottom } = rectEdges(rect);
  let nextLeft = left;
  let nextTop = top;
  let nextRight = right;
  let nextBottom = bottom;

  if (handle.includes('w')) nextLeft = left + delta.x;
  if (handle.includes('e')) nextRight = right + delta.x;
  if (handle.includes('n')) nextTop = top + delta.y;
  if (handle.includes('s')) nextBottom = bottom + delta.y;

  if (options.aspect && options.aspect > 0 && handle.length === 2) {
    // Keep the ratio by growing the axis that moved less.
    const width = Math.abs(nextRight - nextLeft);
    const height = Math.abs(nextBottom - nextTop);
    const wantedHeight = width / options.aspect;
    const wantedWidth = height * options.aspect;
    if (height < wantedHeight) {
      const grow = wantedHeight - height;
      if (handle.includes('n')) nextTop -= grow;
      else nextBottom += grow;
    } else if (width < wantedWidth) {
      const grow = wantedWidth - width;
      if (handle.includes('w')) nextLeft -= grow;
      else nextRight += grow;
    }
  }

  const normalized = normalizeRect({
    x: nextLeft,
    y: nextTop,
    width: nextRight - nextLeft,
    height: nextBottom - nextTop,
  });

  const clamped = clampRect(normalized, bounds);
  if (clamped.width < minimum || clamped.height < minimum) return rect;
  return clamped;
}

export interface SnapTargets {
  x: number[];
  y: number[];
}

export interface SnapResult {
  rect: Rect;
  snappedX: number | null;
  snappedY: number | null;
}

/**
 * Snap a rectangle's edges and centre to guide lines within `threshold`.
 *
 * Nearest candidate wins per axis; the document edges and centre are passed in
 * as targets by the caller so this stays free of document knowledge.
 */
export function snapRect(rect: Rect, targets: SnapTargets, threshold = 6): SnapResult {
  const candidatesX = [rect.x, rect.x + rect.width / 2, rect.x + rect.width];
  const candidatesY = [rect.y, rect.y + rect.height / 2, rect.y + rect.height];

  let snappedX: number | null = null;
  let bestX = threshold + 1;
  for (const target of targets.x) {
    candidatesX.forEach((candidate, index) => {
      const distance = Math.abs(candidate - target);
      if (distance < bestX) {
        bestX = distance;
        snappedX = target - candidate;
        void index;
      }
    });
  }

  let snappedY: number | null = null;
  let bestY = threshold + 1;
  for (const target of targets.y) {
    for (const candidate of candidatesY) {
      const distance = Math.abs(candidate - target);
      if (distance < bestY) {
        bestY = distance;
        snappedY = target - candidate;
      }
    }
  }

  if (bestX > threshold) snappedX = null;
  if (bestY > threshold) snappedY = null;

  return {
    rect: {
      ...rect,
      x: rect.x + (snappedX ?? 0),
      y: rect.y + (snappedY ?? 0),
    },
    snappedX: snappedX === null ? null : rect.x + snappedX,
    snappedY: snappedY === null ? null : rect.y + snappedY,
  };
}

/** Guides plus document edges and centre, ready for snapRect. */
export function defaultSnapTargets(docSize: Size2, guides: readonly Guide[]): SnapTargets {
  return {
    x: [
      0,
      docSize.width / 2,
      docSize.width,
      ...guides.filter((g) => g.axis === 'x').map((g) => g.position),
    ],
    y: [
      0,
      docSize.height / 2,
      docSize.height,
      ...guides.filter((g) => g.axis === 'y').map((g) => g.position),
    ],
  };
}

export interface Size2 {
  width: number;
  height: number;
}

export interface Guide {
  axis: 'x' | 'y';
  position: number;
}

/** Snap a single pointer coordinate to the nearest target within threshold. */
export function snapValue(value: number, targets: readonly number[], threshold = 6): number {
  let best = value;
  let bestDistance = threshold + 1;
  for (const target of targets) {
    const distance = Math.abs(target - value);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = target;
    }
  }
  return bestDistance <= threshold ? best : value;
}
